// src/components/ConfirmDialog.tsx
// Reusable in-app confirmation dialog. Replaces native window.confirm()
// (which shows the browser's "<origin> says" header). Use anywhere we'd
// otherwise prompt the user for a yes/no decision.
//
// Usage:
//   const [confirm, setConfirm] = useState<ConfirmState | null>(null);
//   ...
//   setConfirm({
//     title: 'Delete project?',
//     description: 'It will be moved to Recently Deleted.',
//     confirmLabel: 'Delete',
//     tone: 'danger',
//     onConfirm: async () => { ...do the thing... },
//   });
//   ...
//   <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />

import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';

export type ConfirmTone = 'default' | 'danger' | 'success';

export interface ConfirmState {
  title: string;
  description?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  /** Called when the user clicks confirm. May return a promise — the dialog
   *  shows a loading state while it resolves. */
  onConfirm: () => void | Promise<void>;
}

interface Props {
  state: ConfirmState | null;
  onClose: () => void;
}

export default function ConfirmDialog({ state, onClose }: Props) {
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!state) return;
    setBusy(false);
    // Close on Escape for quick dismissal.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, busy, onClose]);

  if (!state) return null;

  const tone: ConfirmTone = state.tone || 'default';
  const confirmClass = tone === 'danger'
    ? 'bg-red-600 hover:bg-red-700'
    : tone === 'success'
    ? 'bg-green-600 hover:bg-green-700'
    : 'bg-blue-600 hover:bg-blue-700';

  const iconWrapClass = tone === 'danger'
    ? 'bg-red-100 text-red-600'
    : tone === 'success'
    ? 'bg-green-100 text-green-600'
    : 'bg-blue-100 text-blue-600';

  const handleConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await state.onConfirm();
      onClose();
    } catch {
      // Caller is responsible for its own error toast — we just stop spinning.
      setBusy(false);
    }
  };

  return createPortal(
    <div className="gt-themed">
      {/* Backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{ background: 'rgba(15, 29, 20, 0.45)', backdropFilter: 'blur(4px)', zIndex: 200 }}
        onClick={() => { if (!busy) onClose(); }}
      />

      {/* Dialog */}
      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 201 }}>
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            className="relative w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => { if (!busy) onClose(); }}
              disabled={busy}
              aria-label="Close"
              className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-5 pr-12">
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${iconWrapClass}`}>
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    id="confirm-dialog-title"
                    className="text-base font-semibold text-gray-900"
                  >
                    {state.title}
                  </h3>
                  {state.description && (
                    <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">
                      {state.description}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => { if (!busy) onClose(); }}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {state.cancelLabel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={busy}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2 ${confirmClass}`}
              >
                {busy && (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {state.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
